import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useTokenInterceptor } from './hooks/useTokenInterceptor';
import { ResponsiveLayout } from './components/layout/ResponsiveLayout';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { AssessmentPage } from './pages/AssessmentPage';
import { ResultsPage } from './pages/ResultsPage';
import { MyResultsPage } from './pages/MyResultsPage';
import { ProfilesViewer } from './pages/dev/ProfilesViewer';
import ProfilesPage from './pages/ProfilesPage';
import { ResultSimulator } from './pages/dev/ResultSimulator';
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import FeedbackAnalysisPage from './pages/admin/FeedbackAnalysis';
import { ContentVersionManager } from './pages/admin/ContentVersionManager';
import { ContentEditor } from './pages/admin/ContentEditor';

// A inicialização do Firebase foi movida para o arquivo 'firebase.ts'
// para evitar inicializações duplicadas.

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  // Se não autenticado, salvar URL atual antes de redirecionar
  if (!isAuthenticated) {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/login' && currentPath !== '/register' && currentPath !== '/') {
      localStorage.setItem('redirect_after_login', currentPath);
    }
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Redirect authenticated users
const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

// Admin Route Component - TEMPORARY SIMPLIFIED VERSION
const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  
  console.log('🔍 Admin Check (Simplified):', {
    isLoading,
    isAuthenticated,
    user: user ? { 
      email: user.email, 
      subscription_tier: user.subscription_tier,
      SubscriptionTier: user.SubscriptionTier 
    } : null
  });
  
  // Show loading while auth is being initialized
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando permissões...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // TEMPORARY: Allow any authenticated user to access admin
  // Will be fixed later with proper role system
  console.log('✅ Temporary admin access granted to any authenticated user');
  return <>{children}</>;
};

function App() {
  const { initializeAuth, isLoading } = useAuthStore();
  
  // Initialize token interceptor globally
  useTokenInterceptor();

  useEffect(() => {
    // Initialize auth state when the app mounts - only once
    initializeAuth();
  }, []); // Remove initializeAuth from dependencies to prevent loops

  // Show loading spinner while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-blue-900/20 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-slate-600 dark:text-slate-400">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<ResponsiveLayout />}>
          {/* Public Routes */}
          <Route index element={<HomePage />} />
          <Route
            path="login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="assessment"
            element={
              <ProtectedRoute>
                <AssessmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="results"
            element={
              <ProtectedRoute>
                <MyResultsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="results/:assessmentId"
            element={
              <ProtectedRoute>
                <ResultsPage />
              </ProtectedRoute>
            }
          />

          {/* New Profiles Page Route */}
          <Route path="/profiles" element={<ProfilesPage />} />

          {/* Dev Routes */}
          <Route path="/dev/profiles" element={<ProfilesViewer />} />
          <Route path="/dev/simulator" element={<ResultSimulator />} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>

        {/* Admin Routes - Separate layout */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }>
          <Route index element={<AdminDashboard />} />
          
          {/* Content Management Routes */}
          <Route path="content" element={<ContentVersionManager />} />
          <Route path="content/versions" element={<ContentVersionManager />} />
          <Route path="content/versions/:versionId" element={<ContentEditor />} />
          <Route path="content/versions/:versionId/edit" element={<ContentEditor />} />
          <Route path="content/import-export" element={<div>Import/Export Tools - Em desenvolvimento</div>} />
          
          {/* Other Admin Routes */}
          <Route path="users" element={<div>Gestão de Usuários - Em desenvolvimento</div>} />
          <Route path="assessments" element={<div>Gestão de Avaliações - Em desenvolvimento</div>} />
          <Route path="feedbacks" element={<div>Gestão de Feedbacks - Em desenvolvimento</div>} />
          <Route path="analytics" element={<div>Analytics - Em desenvolvimento</div>} />
          <Route path="system" element={<div>Sistema - Em desenvolvimento</div>} />
          <Route path="feedbacks/analysis" element={<FeedbackAnalysisPage />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;